import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { uploadFirmwareDetails, getAllDeviceModels } from "../commonApi";
import { Button } from 'react-bootstrap';
import AsyncSelect from "react-select/async";

function AddFirmwareDialog({ isOpen, onClose, refreshData }) {
    const [firmwareData, setFirmwareData] = useState({
        version: "",
        title: "",
        deviceModelName: "",
        description: "",
        createdBy: "admin",
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSaveDisabled, setIsSaveDisabled] = useState(false); // Replaced isUploading
    const [deviceModels, setDeviceModel] = useState([]);
    const modalRef = useRef(null);
    const saveButtonRef = useRef(null); // New ref for save button

    useEffect(() => {
        if (isOpen) {
            loadDeviceModels();
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const handleClose = () => {
        setFirmwareData({
            version: "",
            title: "",
            deviceModelName: "",
            description: "",
            createdBy: "admin",
        });
        setSelectedFile(null);
        setErrors({});
        onClose();
    };

    const loadDeviceModels = async (inputValue) => {
        try {
            const response = await getAllDeviceModels(0, 1000, 'createdDate', 'desc', inputValue);
            if (response.status === 200) {
                return response?.data?.data?.content.map(item => ({
                    label: item.name.replace(/_/g, " "),
                    value: item.name
                }));
            } else {
                toast.error("Failed to load device models.");
                return [];
            }
        } catch (error) {
            toast.error("Error fetching device models.");
            return [];
        }
    };    

    // Validation patterns
    const VERSION_PATTERN = /^[a-zA-Z0-9._]+$/;
    const TITLE_PATTERN = /^[a-zA-Z0-9._\s]+$/;
    const MAX_DESCRIPTION_LENGTH = 255;
    const ALLOWED_FILE_TYPES = ["bin", "pdf", "jpg", "png"];
    const MAX_FILE_SIZE_MB = 5;

    const validateField = (name, value) => {
        let error = "";
        switch (name) {
            case "version":
                if (!value) {
                    error = "Version is required.";
                } else if (!VERSION_PATTERN.test(value)) {
                    error = "Version can only contain letters, numbers, dot, and underscore.";
                }
                break;
            case "title":
                if (!value) {
                    error = "Name is required.";
                } else if (!TITLE_PATTERN.test(value)) {
                    error = "Title can only contain letters, numbers, spaces, dot, and underscore.";
                }
                break;
            case "deviceModelName":
                if (!value) {
                    error = "Device Type is required.";
                }
                break;
            case "description":
                if (value.length > MAX_DESCRIPTION_LENGTH) {
                    error = "Description cannot exceed 255 characters.";
                }
                break;
            default:
                break;
        }
        return error;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFirmwareData({ ...firmwareData, [name]: value });

        const error = validateField(name, value);
        setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const fileExtension = file.name.split(".").pop().toLowerCase();
            const fileSizeMB = file.size / (1024 * 1024);

            let fileError = "";
            if (!ALLOWED_FILE_TYPES.includes(fileExtension)) {
                fileError = `Invalid file type. Allowed: ${ALLOWED_FILE_TYPES.join(", ")}.`;
            } else if (fileSizeMB > MAX_FILE_SIZE_MB) {
                fileError = `File size should not exceed ${MAX_FILE_SIZE_MB}MB.`;
            }

            setErrors((prevErrors) => ({ ...prevErrors, file: fileError }));
            setSelectedFile(fileError ? null : file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSaveDisabled || (saveButtonRef.current && saveButtonRef.current.disabled)) return;

        // Validate all fields before submission
        const newErrors = {};
        Object.keys(firmwareData).forEach((key) => {
            const error = validateField(key, firmwareData[key]);
            if (error) newErrors[key] = error;
        });
        if (!selectedFile) {
            newErrors.file = "Please select a valid file to upload.";
        }

        if (Object.values(newErrors).some((error) => error)) {
            setErrors(newErrors);
            toast.error("Please fix validation errors before submitting.");
            return;
        }

        if (saveButtonRef.current) {
            saveButtonRef.current.disabled = true; // Immediate DOM disable
        }
        setIsSaveDisabled(true); // Update state for UI feedback

        try {
            await uploadFirmwareDetails(selectedFile, firmwareData);
            toast.success("Firmware uploaded successfully!");
            refreshData();
            handleClose(); // Use handleClose instead of onClose to reset form
        } catch (error) {
            toast.error("Error uploading firmware.");
        } finally {
            if (saveButtonRef.current) {
                saveButtonRef.current.disabled = false; // Re-enable button
            }
            setIsSaveDisabled(false); // Reset state
        }
    };

    return isOpen ? (
        <div className="">
            <div className="fade modal-backdrop show"></div>
            <div className="modal fade show" style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", marginTop: "20px", borderRadius: "5px", marginBottom: "20px" }}>
                <div className="modal-dialog modal-dialog-centered" ref={modalRef}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title h4" style={{ color: "#4340d2" }}>Add Firmware Details</h5>
                            <button className="btn-close" onClick={handleClose}></button>
                        </div>
                        <div className="modal-body" style={{ width: "450px", maxHeight: "400px", overflowY: "auto" }}>
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label className="form-label">Name<span className="required-asterisk" style={{ color: "red" }}>*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="title"
                                        value={firmwareData.title}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {errors.title && <small className="text-danger">{errors.title}</small>}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Version<span className="required-asterisk" style={{ color: "red" }}>*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="version"
                                        value={firmwareData.version}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {errors.version && <small className="text-danger">{errors.version}</small>}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Device Model<span className="required-asterisk" style={{ color: "red" }}>*</span></label>
                                    <AsyncSelect
                                        name="deviceModelName"
                                        loadOptions={loadDeviceModels}
                                        defaultOptions
                                        cacheOptions
                                        value={
                                            firmwareData.deviceModelName
                                                ? { label: firmwareData.deviceModelName.replace(/_/g, " "), value: firmwareData.deviceModelName }
                                                : null
                                        }
                                        onChange={(selectedOption) => {
                                            setFirmwareData({ ...firmwareData, deviceModelName: selectedOption?.value || "" });
                                            const error = validateField("deviceModelName", selectedOption?.value || "");
                                            setErrors((prevErrors) => ({ ...prevErrors, deviceModelName: error }));
                                        }}
                                        isSearchable
                                        placeholder="Search Device Type"
                                    />
                                    {errors.deviceModelName && <small className="text-danger">{errors.deviceModelName}</small>}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Upload File<span className="required-asterisk" style={{ color: "red" }}>*</span></label>
                                    <input type="file" className="form-control" onChange={handleFileChange} required />
                                    {errors.file && <small className="text-danger">{errors.file}</small>}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        value={firmwareData.description}
                                        onChange={handleInputChange}
                                        maxLength={MAX_DESCRIPTION_LENGTH}
                                    />
                                    {errors.description && <small className="text-danger">{errors.description}</small>}
                                </div>

                                <div className="modal-footer">
                                    <button type="button" className="btn light_primary_btn me-3" onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <Button
                                        type="submit"
                                        data-testid="addbutton"
                                        className="btn primary_btn"
                                        ref={saveButtonRef}
                                        disabled={isSaveDisabled}
                                    >
                                        {isSaveDisabled ? "Saving..." : "Save"}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    ) : null;
}

export default AddFirmwareDialog;